import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckCircle2, Send, Loader2, X } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { submissionService } from '../services/submissionService';
import { budgetService } from '../services/budgetService';

// =============================================================================
// SubmitBudgetButton
// A module owner's final "I'm happy with this budget" sign-off for the active
// budget version + site. Drop one on each module's landing page:
//   <SubmitBudgetButton module="MOD_3_MAKE" />
// Self-contained: reads version/site from context, loads its own status, and
// confirms via a modal listing the module, site and version.
// =============================================================================

const MODULE_NAMES = {
  MOD_1_OPEX: 'Opex Entry',
  MOD_2_SUPPLY: 'Purchase',
  MOD_3_MAKE: 'Manufacturing',
  MOD_4_MOVE: 'Distribution',
  MOD_5_CAPEX: 'Capital',
  MOD_6_PROFIT: 'Revenue',
  MOD_8_PEOPLE: 'People',
};

const SubmitBudgetButton = ({ module, moduleName }) => {
  const { selectedVersion, selectedSite, assignments } = useAppContext();
  const ready = !!(selectedVersion && selectedSite);
  const name = moduleName || MODULE_NAMES[module] || module;

  const [status, setStatus] = useState(null); // { submitted, submitted_at, submitted_by, submitted_role }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState('');
  const [versions, setVersions] = useState([]);

  // Friendly site + version labels for the confirm dialog.
  const siteName = useMemo(() => {
    const a = (assignments || []).find((x) => x.site_code === selectedSite);
    return a?.site_name || selectedSite;
  }, [assignments, selectedSite]);

  const versionLabel = useMemo(() => {
    const v = versions.find((x) => x.version_id === selectedVersion);
    if (!v) return selectedVersion;
    return `${v.version_code || v.version_id}${v.description ? ` — ${v.description}` : ''}`;
  }, [versions, selectedVersion]);

  const load = useCallback(async () => {
    if (!ready) { setStatus(null); return; }
    setLoading(true);
    setError('');
    try {
      const r = await submissionService.getSubmission(module);
      if (r?.success) setStatus(r.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load submission status.');
    } finally {
      setLoading(false);
    }
  }, [ready, module]);

  useEffect(() => { load(); }, [load]);

  // Load version list once so the confirm dialog can show a friendly label.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await budgetService.getVersions();
        const list = Array.isArray(res) ? res : res?.data;
        if (!cancelled && Array.isArray(list)) setVersions(list);
      } catch {
        /* fall back to showing the raw version id */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await submissionService.submit(module);
      if (!r?.success) throw new Error(r?.message || 'Submit failed.');
      setConfirmOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Submit failed.');
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await submissionService.withdraw(module);
      if (!r?.success) throw new Error(r?.message || 'Withdraw failed.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Withdraw failed.');
    } finally {
      setBusy(false);
    }
  };

  // --- Not ready: need a version + site selected in the top bar. ---
  if (!ready) {
    return (
      <button
        type="button"
        disabled
        title="Select a budget version and site in the top bar first"
        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed"
      >
        <Send className="w-4 h-4" /> Submit Budget
      </button>
    );
  }

  // --- Already submitted: show who/when + a withdraw option. ---
  if (status?.submitted) {
    const when = status.submitted_at ? new Date(status.submitted_at).toLocaleString() : '';
    return (
      <div className="shrink-0 flex flex-col items-end gap-1">
        <span className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">
          <CheckCircle2 className="w-4 h-4" /> Budget submitted
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400 text-right">
          {status.submitted_by ? `by ${status.submitted_by}` : ''}{when ? ` · ${when}` : ''}
        </span>
        <button
          type="button"
          onClick={withdraw}
          disabled={busy}
          className="text-xs text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:underline disabled:opacity-50"
        >
          {busy ? 'Withdrawing…' : 'Withdraw submission'}
        </button>
        {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
      </div>
    );
  }

  // --- Not submitted: button opens the confirm modal. ---
  return (
    <>
      <button
        type="button"
        onClick={() => { setError(''); setConfirmOpen(true); }}
        disabled={loading}
        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-colors disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Submit Budget
      </button>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-6"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) setConfirmOpen(false); }}
        >
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
              <h3 className="m-0 text-base font-bold text-slate-800 dark:text-slate-100">Submit budget</h3>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={busy}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5">
              <p className="m-0 text-sm text-slate-600 dark:text-slate-300">
                You are about to submit your <span className="font-semibold">{name}</span> budget for:
              </p>

              <dl className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700/60 overflow-hidden">
                {[
                  ['Module', name],
                  ['Site', siteName],
                  ['Version', versionLabel],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-4 px-3 py-2 bg-slate-50/60 dark:bg-slate-800/40">
                    <dt className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{k}</dt>
                    <dd className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100 text-right">{v}</dd>
                  </div>
                ))}
              </dl>

              <p className="m-0 mt-3 text-xs text-slate-500 dark:text-slate-400">
                This is your final acknowledgement that this budget is complete and ready for review.
                You can withdraw it afterwards if something changes.
              </p>

              {error && <p className="m-0 mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  disabled={busy}
                  className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {busy ? 'Submitting…' : 'Submit budget'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubmitBudgetButton;
